import { ISaSmsTenantDetail, SaSmsTier } from '@interfaces/sa-sms';
import { Button, Card, Descriptions, Form, InputNumber, notification, Select } from 'antd';
import { useSaSmsWrite } from 'hooks/use-sa-sms-api';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SaSmsReasonModal } from './sa-sms-reason-modal';

type Values = {
    riskTier: SaSmsTier;
    campaignRecipientLimit: number | null;
    dailySegmentLimit: number | null;
    backlogDays: number | null;
};

type Props = { detail: ISaSmsTenantDetail; canEdit: boolean; onChanged: () => void };

/** Risk tier + per-tenant limit overrides (blank = the tier default). Enterprise needs both limits (checked upstream too). */
export const SmsLimitsForm: React.FC<Props> = ({ detail, canEdit, onChanged }) => {
    const { t, i18n } = useTranslation(['sa-sms']);
    const { send } = useSaSmsWrite();
    const [open, setOpen] = useState(false);
    const cfg = detail.config.config;
    const eff = detail.config.limits;
    const num = (n?: number | null) =>
        n === null || n === undefined ? '-' : n.toLocaleString(i18n.language);

    const confirm = async ({ reason, ...values }: Values & { reason: string }) => {
        await send('put', `/tenants/${detail.tenantId}/config`, {
            riskTier: values.riskTier,
            // `null` resets the limit to the tier default.
            campaignRecipientLimit: values.campaignRecipientLimit ?? null,
            dailySegmentLimit: values.dailySegmentLimit ?? null,
            backlogDays: values.backlogDays ?? null,
            reason,
        });
        notification.success({ message: t('saved') });
        setOpen(false);
        onChanged();
    };

    return (
        <Card
            title={t('detail.limits')}
            extra={canEdit && <Button onClick={() => setOpen(true)}>{t('actions.edit')}</Button>}
        >
            <Descriptions column={1} size="small" bordered>
                <Descriptions.Item label={t('limits.tier')}>
                    {t(`tier.${cfg.risk_tier}`)}
                </Descriptions.Item>
                <Descriptions.Item label={t('limits.campaign')}>
                    {num(eff.campaignRecipientLimit)}{' '}
                    {cfg.campaign_recipient_limit === null && `(${t('limits.tierDefault')})`}
                </Descriptions.Item>
                <Descriptions.Item label={t('limits.daily')}>
                    {num(eff.dailySegmentLimit)}{' '}
                    {cfg.daily_segment_limit === null && `(${t('limits.tierDefault')})`}
                </Descriptions.Item>
                <Descriptions.Item label={t('limits.backlog')}>
                    {num(cfg.backlog_days)}
                </Descriptions.Item>
            </Descriptions>
            <SaSmsReasonModal<Values>
                open={open}
                title={t('detail.limits')}
                initialValues={{
                    riskTier: cfg.risk_tier,
                    campaignRecipientLimit: cfg.campaign_recipient_limit,
                    dailySegmentLimit: cfg.daily_segment_limit,
                    backlogDays: cfg.backlog_days,
                }}
                onCancel={() => setOpen(false)}
                onConfirm={confirm}
            >
                <Form.Item name="riskTier" label={t('limits.tier')}>
                    <Select
                        options={(['new', 'standard', 'enterprise'] as const).map((v) => ({
                            value: v,
                            label: t(`tier.${v}`),
                        }))}
                    />
                </Form.Item>
                <Form.Item
                    name="campaignRecipientLimit"
                    label={t('limits.campaign')}
                    extra={t('limits.blankHint')}
                >
                    <InputNumber min={1} max={10000000} precision={0} style={{ width: '100%' }} />
                </Form.Item>
                <Form.Item
                    name="dailySegmentLimit"
                    label={t('limits.daily')}
                    extra={t('limits.blankHint')}
                >
                    <InputNumber min={1} max={10000000} precision={0} style={{ width: '100%' }} />
                </Form.Item>
                <Form.Item
                    name="backlogDays"
                    label={t('limits.backlog')}
                    extra={t('limits.blankHint')}
                >
                    <InputNumber min={1} max={365} precision={0} style={{ width: '100%' }} />
                </Form.Item>
            </SaSmsReasonModal>
        </Card>
    );
};
